import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Button, Tooltip, IconButton, TextField } from '@mui/material';
import useWrittenDirectives from '../../../_data_sources/WrittenDirectives/useWrittenDirectives';
import { CircularProgress } from '@mui/material';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import { Jurisdiction } from 'types/jurisdiction';
import { neutral, primary } from '_lex/_foundations/phxColors';
import { ChevronRight, Add, ArrowBack, AttachFile, Delete } from '@mui/icons-material';
import { AppContext } from 'views/App/context';
import { WrittenDirective } from '../../../../_types/awb';
import { translate as t } from 'hooks/useTranslations';
import RightDrawer from '_lex/_layouts/TakeoverLayout/RightDrawer';
import useProofsOfComplianceOrg from '../../../_data_sources/ProofsOfCompliance/useProofsOfComplianceOrg';

interface WrittenDirectivesSummaryProps {
  standardId: string;
  jurisdictions: Jurisdiction[];
}

const WrittenDirectivesSummary: React.FC<WrittenDirectivesSummaryProps> = ({ standardId, jurisdictions }) => {
  const { id: accreditationId, versionId } = useParams();
  const navigate = useNavigate();
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState<string>(jurisdictions[0]?.id ?? '');
  const [panel, setPanel] = useState<'directives' | 'proofs'>('directives');
  const [proofNotes, setProofNotes] = useState('');
  const { currentOrganizationId, hasPermission, isFeatureEnabled, currentProfile } = useContext(AppContext);

  const hasProofsPanel = isFeatureEnabled('awbPremiumDemo');

  const authorName =
    currentProfile?.user?.firstName && currentProfile?.user?.lastName
      ? `${currentProfile.user.firstName} ${currentProfile.user.lastName}`
      : currentProfile?.user?.email ?? 'User';

  const {
    data: writtenDirectives,
    isLoading,
    isFetching,
  } = useWrittenDirectives(currentOrganizationId, accreditationId!, versionId!, standardId, selectedJurisdictionId);

  const { proofs, addProof, removeProof } = useProofsOfComplianceOrg(
    accreditationId ?? '',
    versionId ?? '',
    standardId
  );

  const canCreateWrittenDirective = hasPermission('createWrittenDirective', currentOrganizationId);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    addProof({ name: file.name, size: file.size, type: file.type }, proofNotes, authorName);
    setProofNotes('');
    // Reset input so the same file can be re-selected if removed
    e.target.value = '';
  };

  const handleRemoveProof = (id: string) => {
    if (!window.confirm(t('awb.complianceChecklist.confirmRemove'))) return;
    removeProof(id);
  };

  const directivesHeaderActions = (
    <Tooltip
      title={t('awb.standards.detail.rightDrawer.add')}
      placement='left'
    >
      <Button
        size='small'
        className='xs icon-only'
        variant='outlined'
        color='neutral'
        startIcon={<Add />}
        disabled={!canCreateWrittenDirective}
        onClick={() =>
          navigate(
            `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${selectedJurisdictionId}/written-directives`
          )
        }
      />
    </Tooltip>
  );

  const directivesContent = (
    <Stack spacing={2}>
      {jurisdictions.length > 1 && (
        <Box sx={{ py: 2 }}>
          <JurisdictionPicker
            id='jurisdiction-picker'
            value={[selectedJurisdictionId]}
            onChange={(value) => setSelectedJurisdictionId(value[0])}
            allowBlank={false}
            allowMultiple={false}
            filterIds={jurisdictions.map((j) => j.id)}
            size='small'
            sx={{ pt: 0 }}
            prefix='Jurisdiction'
          />
        </Box>
      )}
      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !writtenDirectives?.length ? (
        <Box sx={{ py: 2 }}>
          <Typography variant='bodySm' color='text.secondary'>
            No written directives found
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {writtenDirectives.map((directive: WrittenDirective) => (
            <Box
              key={directive.id}
              onClick={() =>
                navigate(
                  `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${selectedJurisdictionId}/written-directives/${directive.id}`
                )
              }
              sx={{
                py: 2,
                borderBottom: `1px solid ${neutral[100]}`,
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: neutral[50] },
              }}
            >
              <Stack sx={{ minWidth: 0 }}>
                <Typography
                  variant='labelSm'
                  noWrap
                  sx={{ color: neutral[500], fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {directive.contentHeading} {directive.contentTitle}
                </Typography>
                <Typography
                  variant='bodySm'
                  noWrap
                  sx={{ color: neutral[500], textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {directive.manualTitle}
                </Typography>
              </Stack>
              <ChevronRight sx={{ color: neutral[500], fontSize: 24 }} />
            </Box>
          ))}
        </Stack>
      )}

      {hasProofsPanel && (
        <Box sx={{ pt: 2, borderTop: `1px solid ${neutral[100]}` }}>
          <Button
            variant='text'
            color='primary'
            size='small'
            startIcon={<AttachFile />}
            onClick={() => setPanel('proofs')}
            sx={{ px: 0 }}
          >
            {t('awb.complianceChecklist.proofs')}
            {proofs.length > 0 && ` (${proofs.length})`}
          </Button>
        </Box>
      )}
    </Stack>
  );

  const proofsContent = (
    <Stack spacing={2}>
      {proofs.length === 0 ? (
        <Typography variant='bodySm' color='text.secondary'>
          {t('awb.complianceChecklist.noProofs')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {proofs.map((proof) => (
            <Box
              key={proof.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 1.5,
                borderBottom: `1px solid ${neutral[100]}`,
              }}
            >
              <AttachFile sx={{ fontSize: 18, color: neutral[400], mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='labelSm' noWrap sx={{ color: neutral[800], fontWeight: 600 }}>
                  {proof.fileName}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {t('awb.complianceChecklist.uploadedBy')} {proof.uploadedByName} ·{' '}
                  {new Date(proof.uploadedAt).toLocaleDateString()}
                </Typography>
                {proof.notes && (
                  <Typography variant='bodySm' color='text.secondary' sx={{ mt: 0.25 }}>
                    {proof.notes}
                  </Typography>
                )}
              </Box>
              <IconButton
                size='small'
                color='error'
                onClick={() => handleRemoveProof(proof.id)}
                aria-label={t('awb.complianceChecklist.remove')}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* Upload section */}
      <Box sx={{ pt: 1 }}>
        <TextField
          fullWidth
          size='small'
          placeholder={t('awb.complianceChecklist.notes')}
          value={proofNotes}
          onChange={(e) => setProofNotes(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Button
          variant='outlined'
          color='neutral'
          size='small'
          component='label'
          startIcon={<Add />}
          fullWidth
        >
          {t('awb.complianceChecklist.uploadProof')}
          <input type='file' hidden onChange={handleFileUpload} />
        </Button>
      </Box>
    </Stack>
  );

  return (
    <RightDrawer
      header={
        panel === 'proofs' ? (
          <Stack direction='row' alignItems='center' gap={0.5}>
            <IconButton size='small' onClick={() => setPanel('directives')} aria-label='Back'>
              <ArrowBack sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant='subtitleLg'>{t('awb.complianceChecklist.proofs')}</Typography>
          </Stack>
        ) : (
          t('awb.standards.detail.rightDrawer.title')
        )
      }
      headerActions={panel === 'directives' ? directivesHeaderActions : undefined}
      content={panel === 'directives' ? directivesContent : proofsContent}
      open={true}
    />
  );
};

export default WrittenDirectivesSummary;
